import { z } from 'zod';
import { userStatusSchema } from './userEnums.js';

/**
 * A user's profile as other people see it — the `user:update` payload sent to
 * every server the user is in. Only what peers render: name, avatar, team,
 * signature and a public status. The owner's settings (DM privacy, read
 * history, onboarding, language, platform id, account age) go to the owner's
 * own devices only, as `me:update`.
 *
 * Strict, so a field added to the full user cannot reach peers by accident
 * through this contract.
 */
export const peerUserStatusSchema = userStatusSchema.exclude(['INVISIBLE']);

export const peerUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  teamTag: z.string().nullable(),
  teamName: z.string().nullable(),
  /** INVISIBLE means "appear offline", so peers are sent OFFLINE. */
  status: peerUserStatusSchema,
  bio: z.string().nullable(),
  statusText: z.string().nullable(),
}).strict();

export type PeerUser = z.infer<typeof peerUserSchema>;

/** The fields {@link toPeerUser} reads; a full user row has more, all dropped. */
export interface PeerUserSource {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  teamTag?: string | null;
  teamName?: string | null;
  status: z.infer<typeof userStatusSchema>;
  bio?: string | null;
  statusText?: string | null;
}

/** Project a user onto what peers may see. Copies named fields only. */
export function toPeerUser(user: PeerUserSource): PeerUser {
  return {
    id: user.id,
    username: user.username,
    displayName: user.displayName,
    avatarUrl: user.avatarUrl,
    teamTag: user.teamTag ?? null,
    teamName: user.teamName ?? null,
    status: user.status === 'INVISIBLE' ? 'OFFLINE' : user.status,
    bio: user.bio ?? null,
    statusText: user.statusText ?? null,
  };
}
